import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ServiceDetailDto, UpdateServiceInput } from '@cpm/shared';
import {
  ArrowLeft,
  Boxes,
  ExternalLink,
  Link2,
  Pencil,
  Play,
  RefreshCw,
  Square,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { Modal } from '../components/Modal.js';
import { EnvironmentPanel, StoragePanel } from '../components/resource-tabs.js';
import { ResourceMetricsPanel } from '../components/metrics-panels.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Spinner,
  Tabs,
  statusTone,
} from '../components/ui.js';

/** Coolify-reported fqdn values are rendered as links only when http(s). */
function safeHref(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

function EditServiceForm({
  detail,
  onDone,
}: {
  detail: ServiceDetailDto;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(detail.resource.name);
  const [description, setDescription] = useState(detail.live?.description ?? '');

  const update = useMutation({
    mutationFn: (input: UpdateServiceInput) =>
      api.patch(`/api/services/${detail.resource.id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['services', detail.resource.id] });
      onDone();
    },
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate({ name, description: description || null });
      }}
    >
      <div>
        <Label htmlFor="svc-name">Name</Label>
        <Input id="svc-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div>
        <Label htmlFor="svc-desc">Description</Label>
        <Input id="svc-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
      {update.error != null && <ErrorNote error={update.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={update.isPending}>
          Save changes
        </Button>
      </div>
    </form>
  );
}

function OverviewTab({ detail }: { detail: ServiceDetailDto }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [linkUuid, setLinkUuid] = useState('');
  const id = detail.resource.id;
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['services', id] });

  const act = useMutation({
    mutationFn: (action: 'start' | 'stop' | 'restart') => api.post(`/api/services/${id}/${action}`),
    onSuccess: invalidate,
  });
  const link = useMutation({
    mutationFn: () => api.post(`/api/services/${id}/link`, { coolifyUuid: linkUuid }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/services/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      void navigate(`/subscriptions/${detail.resource.subscriptionId}`);
    },
  });

  const canAct = detail.features.deployments && detail.resource.coolifyUuid !== null;
  const live = detail.live;
  const domains = (detail.resource.details?.domains ?? []) as string[];
  const templateSlug = detail.resource.details?.templateSlug as string | undefined;

  return (
    <div className="space-y-4">
      {(act.error ?? remove.error) != null && <ErrorNote error={act.error ?? remove.error} />}

      {!detail.resource.coolifyUuid && (
        <Card>
          <CardHeader
            title="Not linked to Coolify"
            description="The panel reserved this service but has no Coolify UUID for it — paste the UUID from Coolify to repair the link."
          />
          <div className="flex flex-wrap items-center gap-2 px-5 py-4">
            <Input
              className="max-w-sm"
              placeholder="Coolify service UUID"
              value={linkUuid}
              onChange={(e) => setLinkUuid(e.target.value)}
            />
            <Button loading={link.isPending} disabled={!linkUuid.trim()} onClick={() => link.mutate()}>
              <Link2 className="size-4" /> Link
            </Button>
            {link.error != null && <ErrorNote error={link.error} />}
          </div>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader title="Status" />
          <div className="space-y-3 px-5 py-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-500 dark:text-slate-400">State</span>
              <Badge tone={statusTone(detail.resource.status)}>{detail.resource.status}</Badge>
            </div>
            {domains.length > 0 && (
              <div>
                <p className="text-slate-500 dark:text-slate-400">Domains</p>
                {domains.map((domain) => {
                  const href = safeHref(domain);
                  return href ? (
                    <a
                      key={domain}
                      href={href}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="flex items-center gap-1 break-all font-medium text-brand-600 hover:underline dark:text-brand-400"
                    >
                      {domain} <ExternalLink className="size-3.5 shrink-0" />
                    </a>
                  ) : (
                    <span key={domain} className="break-all font-medium">
                      {domain}
                    </span>
                  );
                })}
              </div>
            )}
            {detail.liveError && (
              <p className="text-xs text-amber-600 dark:text-amber-400">{detail.liveError}</p>
            )}
            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3 dark:border-slate-800">
              <Button variant="secondary" disabled={!canAct} loading={act.isPending} onClick={() => act.mutate('start')}>
                <Play className="size-4" /> Start
              </Button>
              <Button variant="secondary" disabled={!canAct} onClick={() => act.mutate('restart')}>
                <RefreshCw className="size-4" /> Restart
              </Button>
              <Button variant="secondary" disabled={!canAct} onClick={() => act.mutate('stop')}>
                <Square className="size-4" /> Stop
              </Button>
            </div>
            {!detail.features.deployments && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Lifecycle actions are disabled — the plan does not include deployments.
              </p>
            )}
          </div>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title="Service stack"
            description={
              templateSlug
                ? `Created from the "${templateSlug}" template`
                : detail.resource.details?.customCompose
                  ? 'Created from a custom docker-compose file'
                  : undefined
            }
            actions={
              detail.resource.coolifyUuid && (
                <Button variant="secondary" onClick={() => setEditOpen(true)}>
                  <Pencil className="size-4" /> Edit
                </Button>
              )
            }
          />
          {live && live.components.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-5 py-3 font-medium">Container</th>
                    <th className="px-5 py-3 font-medium">Kind</th>
                    <th className="px-5 py-3 font-medium">Image</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {live.components.map((component, index) => (
                    <tr key={`${component.kind}-${component.name}-${index}`}>
                      <td className="px-5 py-3 font-medium">
                        {component.fqdn && safeHref(component.fqdn) ? (
                          <a
                            href={safeHref(component.fqdn)!}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center gap-1 text-brand-600 hover:underline dark:text-brand-400"
                          >
                            {component.name} <ExternalLink className="size-3.5" />
                          </a>
                        ) : (
                          component.name
                        )}
                      </td>
                      <td className="px-5 py-3 capitalize text-slate-600 dark:text-slate-300">
                        {component.kind}
                      </td>
                      <td className="max-w-xs truncate px-5 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
                        {component.image ?? '—'}
                      </td>
                      <td className="px-5 py-3">
                        {component.status ? (
                          <Badge tone={statusTone(component.status)}>{component.status}</Badge>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              title="No live stack information"
              description={detail.liveError ?? 'Coolify has not reported the stack containers yet.'}
            />
          )}
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Danger zone"
          description="Deletes the service in Coolify (all stack containers and volumes), then releases the plan quota. Admins only."
        />
        <div className="px-5 py-4">
          <Button
            variant="danger"
            loading={remove.isPending}
            onClick={() => {
              if (
                window.confirm(
                  'Delete this service? The whole stack is torn down in Coolify (containers and volumes) and removed from the ledger.',
                )
              ) {
                remove.mutate();
              }
            }}
          >
            <Trash2 className="size-4" /> Delete service
          </Button>
        </div>
      </Card>

      <Modal title="Edit service" open={editOpen} onClose={() => setEditOpen(false)}>
        <EditServiceForm detail={detail} onDone={() => setEditOpen(false)} />
      </Modal>
    </div>
  );
}

function LogsNotice({ detail }: { detail: ServiceDetailDto }) {
  return (
    <Card>
      <CardHeader
        title="Runtime logs"
        description="The Coolify API does not expose runtime logs for services — only application logs. Use Coolify's own UI to inspect them."
      />
      <div className="px-5 py-4">
        {detail.coolifyBaseUrl ? (
          <a
            href={detail.coolifyBaseUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
          >
            Open Coolify <ExternalLink className="size-4" />
          </a>
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-400">Coolify is not configured.</p>
        )}
      </div>
    </Card>
  );
}

export function ServiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState('overview');

  const detail = useQuery({
    queryKey: ['services', id],
    queryFn: () => api.get<ServiceDetailDto>(`/api/services/${id}`),
    enabled: Boolean(id),
  });

  if (detail.isPending) return <Spinner />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  const data = detail.data;
  const linked = data.resource.coolifyUuid !== null;

  return (
    <div>
      <Link
        to={`/subscriptions/${data.subscription.id}`}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <ArrowLeft className="size-4" /> {data.subscription.name}
      </Link>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Boxes className="size-6 text-slate-400" />
            {data.resource.name}
            <Badge tone={statusTone(data.resource.status)}>{data.resource.status}</Badge>
          </span>
        }
        description={
          <>
            Service of{' '}
            <Link
              to={`/clients/${data.subscription.clientId}`}
              className="text-brand-600 hover:underline dark:text-brand-400"
            >
              {data.subscription.clientName}
            </Link>{' '}
            · subscription{' '}
            <Link
              to={`/subscriptions/${data.subscription.id}`}
              className="text-brand-600 hover:underline dark:text-brand-400"
            >
              {data.subscription.name}
            </Link>
          </>
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'overview', label: 'Overview' },
          { key: 'metrics', label: 'Metrics' },
          { key: 'environment', label: 'Environment', disabled: !linked },
          { key: 'storage', label: 'Storage', disabled: !linked },
          { key: 'logs', label: 'Logs' },
        ]}
      />

      {tab === 'overview' && <OverviewTab detail={data} />}
      {/* Services always answer "unsupported" here — the honest notice
          (Sentinel collects nothing for service templates), never zeros. */}
      {tab === 'metrics' && <ResourceMetricsPanel resourceId={data.resource.id} />}
      {tab === 'environment' && (
        <EnvironmentPanel
          apiBase={`/api/services/${data.resource.id}`}
          queryKey={['services', data.resource.id, 'envs']}
          enabled={linked}
          featureEnabled={data.features.environment_variables ?? false}
          description="Applied on the next restart"
        />
      )}
      {tab === 'storage' && (
        <StoragePanel
          apiBase={`/api/services/${data.resource.id}`}
          queryKey={['services', data.resource.id, 'storages']}
          enabled={linked}
        />
      )}
      {tab === 'logs' && <LogsNotice detail={data} />}
    </div>
  );
}
